import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useProveedores } from '../hooks/useProveedores';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { Confirm } from '../components/Confirm';
import { HelpTip } from '../components/HelpTip';
import { api, extractErrorMessage } from '../api/client';
import { Skeleton } from '../components/Skeleton';

interface StatsCard {
  label: string;
  value: string | number;
  icon: string;
  color: string;
  loading: boolean;
}

export function DashboardPage() {
  // Fetch summary stats from the API
  const { data: proveedores, loading: loadingProveedores } = useProveedores();
  const { data: oc, loading: loadingOc } = useApi<any[]>('/api/v3/compras');
  const { data: remitos, loading: loadingRemitos } = useApi<any[]>('/api/v3/remitos');
  const { data: facturas, loading: loadingFacturas } = useApi<any[]>('/api/v3/facturas');
  const { data: ocPendientes } = useApi<any[]>('/api/v3/compras?estado=pendiente&limit=10');
  const { data: ocParciales } = useApi<any[]>('/api/v3/compras?estado=parcial&limit=10');
  const { data: remitosRecientes } = useApi<any[]>('/api/v3/remitos?limit=5');

  const { can } = useAuth();
  const toast = useToast();
  const [backupConfirmOpen, setBackupConfirmOpen] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const stats: StatsCard[] = [
    {
      label: 'Proveedores',
      value: proveedores?.length ?? 0,
      loading: loadingProveedores,
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      color: 'bg-blue-500',
    },
    {
      label: 'Ordenes de Compra',
      value: oc?.length ?? 0,
      loading: loadingOc,
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
      color: 'bg-green-500',
    },
    {
      label: 'Remitos',
      value: remitos?.length ?? 0,
      loading: loadingRemitos,
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      color: 'bg-amber-500',
    },
    {
      label: 'Facturas',
      value: facturas?.length ?? 0,
      loading: loadingFacturas,
      icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
      color: 'bg-purple-500',
    },
  ];

  const handleBackup = async () => {
    setBackupConfirmOpen(false);
    setBackingUp(true);
    try {
      const res = await api.post<{
        success: boolean;
        filename: string;
        path: string;
        size: number;
      }>('/api/v3/admin/backup', {});
      const sizeMB = (res.size / (1024 * 1024)).toFixed(2);
      toast.addToast(
        `Backup creado: ${res.filename} (${sizeMB} MB)`,
        'success',
      );
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setBackingUp(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Dashboard</h1><HelpTip tips={['Vista general del ERP. Cada tarjeta muestra un indicador clave','Hacé clic en cualquier módulo del menú lateral para gestionar: artículos, compras, remitos, facturas, pagos','Los números se actualizan automáticamente al crear o modificar registros']} />
          <p className="text-slate-500 text-sm mt-1">Resumen general del modulo de compras</p>
        </div>
        {can('admin', 'crear') && (
          <button
            onClick={() => setBackupConfirmOpen(true)}
            disabled={backingUp}
            className="shrink-0 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            title="Generar un backup de la base de datos"
          >
            {backingUp ? '⏳ Creando backup...' : '📦 Hacer Backup'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className={`${s.color} text-white p-3 rounded-lg`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={s.icon} />
                </svg>
              </div>
              <div>
                {s.loading ? (
                  <>
                    <Skeleton className="h-7 w-12 mb-1" />
                    <Skeleton className="h-3 w-20" />
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-slate-800">{s.value}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* OCs pendientes de recepción */}
      {can('compras', 'ver') && (() => {
        const pendientes = ocPendientes ?? [];
        const parciales = ocParciales ?? [];
        const todas = [...parciales, ...pendientes];
        if (todas.length === 0) return null;
        return (
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700">
                OCs pendientes de recepción
                <span className="ml-2 bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">{todas.length}</span>
              </h2>
              <a href="/erp/compras" className="text-xs text-brand-600 hover:underline">Ver todas →</a>
            </div>
            <div className="space-y-2">
              {todas.slice(0, 8).map((o: any) => (
                <div key={o.id} className="flex items-center justify-between text-sm py-1.5 px-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{o.numeroOrden ?? `#${o.id}`}</span>
                    <span className="text-slate-500">{o.proveedor?.nombre ?? `ID ${o.proveedorId}`}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${o.estado==='parcial'?'bg-blue-100 text-blue-700':'bg-amber-100 text-amber-700'}`}>{o.estado}</span>
                  </div>
                  <span className="text-xs text-slate-400">{new Date(o.fecha).toLocaleDateString('es-AR')}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Remitos recientes — notificación para el admin */}
      {can('remitos', 'ver') && remitosRecientes && remitosRecientes.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Remitos recientes</h2>
            <a href="/erp/remitos" className="text-xs text-brand-600 hover:underline">Ver todos →</a>
          </div>
          <div className="space-y-2">
            {remitosRecientes.slice(0, 5).map((r: any) => (
              <div key={r.id} className="flex items-center justify-between text-sm py-1.5 px-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{r.numeroRemito ?? `#${r.id}`}</span>
                  <span className="text-slate-500">{r.proveedor?.nombre ?? r.cliente?.nombre ?? (r.proveedorId ? `ID ${r.proveedorId}` : '—')}</span>
                  {r.ordenCompraId && <span className="text-xs text-slate-400">OC #{r.ordenCompraId}</span>}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.estado==='conciliado'?'bg-green-100 text-green-700':'bg-slate-100 text-slate-500'}`}>{r.estado}</span>
                </div>
                <span className="text-xs text-slate-400">{r.fecha ? new Date(r.fecha).toLocaleDateString('es-AR') : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Confirm
        open={backupConfirmOpen}
        title="Crear backup de la base de datos"
        message="¿Crear backup de la base de datos ahora?"
        confirmLabel="Crear Backup"
        onConfirm={handleBackup}
        onCancel={() => setBackupConfirmOpen(false)}
      />
    </div>
  );
}
